// src/utils/relicVault/collectionApi.js
//
// The Collection Store's backend. Everything the store shows or changes comes
// from here: the catalogs (guilds, guardians, relics, badges), what the learner
// owns, the five relic slots on offer, and every purchase, sale and refresh.
//
// The server is authoritative. Prices, the store rotation and the daily refresh
// allowance are its to decide - the client sends no price and no slot, only
// which relic or guild the learner tapped. Every write returns `newBalance`,
// which is the balance to show afterwards.
//
// Catalog routes are public; everything under /mine, /store and the writes take
// the learner from the bearer token.

import {
  QUIKLEARN_BACKEND_URL,
  API_KEY,
} from 'config/app.config';

const buildHeaders = (token, includeAuth = true) => {
  const headers = { 'Content-Type': 'application/json' };
  if (API_KEY && String(API_KEY).toLowerCase() !== 'dummy') {
    headers['x-api-key'] = API_KEY;
  }
  if (includeAuth && token) headers.Authorization = `Bearer ${token}`;
  return headers;
};

const baseUrl = () => String(QUIKLEARN_BACKEND_URL || '').replace(/\/$/, '');

const safeParseJson = async response => {
  try {
    const text = await response.text();
    return text ? JSON.parse(text) : null;
  } catch (_) {
    return null;
  }
};

/**
 * One call. Returns the response's `data`, or throws with the server's message
 * so the UI can show why a purchase was refused rather than a generic failure.
 */
const call = async (endpoint, { token, method = 'GET', body } = {}) => {
  const url = `${baseUrl()}${endpoint}`;
  const response = await fetch(url, {
    method,
    headers: buildHeaders(token, true),
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  const payload = await safeParseJson(response);

  if (!response.ok) {
    const message =
      payload?.message || payload?.error || response.statusText || 'Request failed';
    const error = new Error(message);
    error.status = response.status;
    throw error;
  }
  return payload?.data ?? null;
};

// ---------------------------------------------------------------------------
// Catalogs. Public, cached server-side for 10 minutes, and unchanged between
// learners - so the store fetches them once per session, in parallel.
// ---------------------------------------------------------------------------

export const fetchGuilds = ({ token } = {}) =>
  call('/api/guilds', { token }).then(d => d?.guilds || []);

export const fetchGuardians = ({ token } = {}) =>
  call('/api/guardians', { token }).then(d => d?.guardians || []);

export const fetchBadgeCatalog = ({ token } = {}) =>
  call('/api/badges', { token }).then(d => d?.categories || []);

/** `{ sheetGrid: {columns, rows}, rarities: [...], relics: [...] }` */
export const fetchRelicCatalog = ({ token } = {}) =>
  call('/api/relics', { token }).then(d => d || { sheetGrid: null, rarities: [], relics: [] });

/**
 * Everything the store needs to render, in one go. The four catalogs are
 * independent, so a slow one never holds up the others.
 */
export const fetchCatalog = async ({ token } = {}) => {
  const [guilds, guardians, relics, badgeCategories] = await Promise.all([
    fetchGuilds({ token }),
    fetchGuardians({ token }),
    fetchRelicCatalog({ token }),
    fetchBadgeCatalog({ token }),
  ]);
  return { guilds, guardians, relics, badgeCategories };
};

// ---------------------------------------------------------------------------
// What this learner owns.
// ---------------------------------------------------------------------------

/** `{ ownedRelicIds }` */
export const fetchMyRelics = ({ token }) => call('/api/relics/mine', { token });

/** `{ ownedGuildIds, equippedGuildId, equippedUnityGuardianId }` */
export const fetchMyGuardians = ({ token }) => call('/api/guardians/mine', { token });

/**
 * The learner's earned badges, as `{ badgeId, earnedAt }`.
 *
 * The route answers `data: { earned: [...] }`. The other `/mine` routes answer their object
 * directly, which is why this one is easy to get wrong - and getting it wrong is silent, since
 * a missing key just reads as "no badges earned".
 */
export const fetchMyBadges = ({ token }) =>
  call('/api/badges/mine', { token }).then(d =>
    Array.isArray(d) ? d : d?.earned || [],
  );

/**
 * The guided first visit (Sol player-tour Q1 D): `{ eligible, steps: { slide, tutor, quiz, controls } }` -
 * whether this learner's account gets the tour, and which badge-earning steps are already done.
 */
export const fetchFirstVisitStatus = ({ token }) => call('/api/badges/onboarding', { token });

/**
 * A first-visit step done ('slide' | 'quiz' | 'controls'): the server awards that step's badge and pays it once.
 * `{ badgeId, awarded, coinsPaid }`; reporting a step again awards and pays nothing.
 */
/** A Quikbase control done in the first visit ('walk' | 'jump' | 'look' | 'fly'): the server pays it once. */
export const reportFirstVisitControl = ({ token, control }) =>
  call(`/api/badges/onboarding/control/${encodeURIComponent(control)}`, { token, method: 'POST', body: {} });

export const reportFirstVisitStep = ({ token, step }) =>
  call(`/api/badges/onboarding/${encodeURIComponent(step)}`, { token, method: 'POST', body: {} });

/** `{ slotRelicIds, refreshesUsed, refreshesLeft, nextRefreshCost }` */
export const fetchStore = ({ token }) => call('/api/relics/store', { token });

/**
 * The learner's whole vault state, as the store opens. Ownership calls need a
 * token; without one the store still renders the catalog, empty.
 */
export const fetchVaultState = async ({ token }) => {
  const [relics, guardians, badges, store] = await Promise.all([
    fetchMyRelics({ token }),
    fetchMyGuardians({ token }),
    fetchMyBadges({ token }),
    fetchStore({ token }),
  ]);
  return {
    ownedRelicIds: relics?.ownedRelicIds || [],
    ownedCompanionIds: guardians?.ownedGuildIds || [],
    equippedCompanionId: guardians?.equippedGuildId || 'none',
    // Unity spawns the guardian by this id, so it has to survive a reload as
    // well as an equip; without it the avatar comes back with no guardian.
    equippedUnityGuardianId: guardians?.equippedUnityGuardianId || 'none',
    ownedBadgeIds: (badges || []).map(b => b.badgeId).filter(Boolean),
    store: store || null,
  };
};

// ---------------------------------------------------------------------------
// Writes. Each is idempotent server-side, keyed so a double tap is charged once.
// ---------------------------------------------------------------------------

/** `{ newBalance, ownedRelicIds }` */
export const purchaseRelic = ({ token, relicId }) =>
  call(`/api/relics/${encodeURIComponent(relicId)}/purchase`, { token, method: 'POST', body: {} });

/** `{ newBalance, refund, ownedRelicIds }` */
export const sellRelic = ({ token, relicId }) =>
  call(`/api/relics/${encodeURIComponent(relicId)}/sell`, { token, method: 'POST', body: {} });

/** `{ slotRelicIds, refreshesUsed, refreshesLeft, nextRefreshCost, newBalance }` */
export const refreshStore = ({ token }) =>
  call('/api/relics/store/refresh', { token, method: 'POST', body: {} });

/** `{ newBalance, ownedGuildIds, equippedGuildId, equippedUnityGuardianId }` */
export const purchaseGuardian = ({ token, guildId }) =>
  call(`/api/guardians/${encodeURIComponent(guildId)}/purchase`, {
    token,
    method: 'POST',
    body: {},
  });

/** `{ ownedGuildIds, equippedGuildId, equippedUnityGuardianId }`; guildId or "none". */
export const equipGuardian = ({ token, guildId }) =>
  call('/api/guardians/equip', { token, method: 'POST', body: { guildId } });
